import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ContentCopy as DuplicateIcon,
} from '@mui/icons-material';
import { ValidationSummary, FieldMapping, ImportOptions } from '../../../types/import.types';
import { importService } from '../../../services/import.service';
import toast from 'react-hot-toast';

interface ValidationStepProps {
  jobId: string;
  fieldMapping: FieldMapping;
  options: ImportOptions;
  onComplete: (data: ValidationSummary) => void;
  onBack: () => void;
}

export const ValidationStep: React.FC<ValidationStepProps> = ({
  jobId,
  fieldMapping,
  options,
  onComplete,
  onBack,
}) => {
  const [validating, setValidating] = useState(false);
  const [validationSummary, setValidationSummary] = useState<ValidationSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localOptions, setLocalOptions] = useState<ImportOptions>(options);

  useEffect(() => {
    validateImport();
  }, []);

  const validateImport = async () => {
    console.log('=== VALIDATION STEP DEBUG ===');
    console.log('Job ID:', jobId);
    console.log('Field Mapping:', fieldMapping);
    console.log('Options:', localOptions);
    
    setValidating(true);
    setError(null);

    try {
      console.log('Calling validateImport service...');
      const summary = await importService.validateImport(
        jobId,
        fieldMapping,
        localOptions
      );
      
      console.log('Validation summary received:', summary);
      setValidationSummary(summary);
      toast.success('Validation complete!');
    } catch (err) {
      console.error('Validation error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to validate import';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  const handleContinue = () => {
    if (validationSummary) {
      onComplete(validationSummary);
    }
  };

  const handleOptionsChange = (key: keyof ImportOptions, value: any) => {
    setLocalOptions((prev) => ({ ...prev, [key]: value }));
  };

  const canProceed = validationSummary && 
    (validationSummary.summary.valid > 0 || validationSummary.summary.warnings > 0);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Validate Import Data
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        Validating your data for errors, warnings, and duplicates...
      </Typography>

      {/* Import Options */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Import Options
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Default Status</InputLabel>
              <Select
                value={localOptions.defaultStatus}
                label="Default Status"
                onChange={(e) => handleOptionsChange('defaultStatus', e.target.value)}
              >
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="qualified">Qualified</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Default Priority</InputLabel>
              <Select
                value={localOptions.defaultPriority}
                label="Default Priority"
                onChange={(e) => handleOptionsChange('defaultPriority', e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={localOptions.assignToCreator}
                  onChange={(e) => handleOptionsChange('assignToCreator', e.target.checked)}
                />
              }
              label="Assign all leads to me"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Validation Progress */}
      {validating && (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Validating Data...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This may take a few moments for large files
          </Typography>
        </Paper>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Validation Failed
          </Typography>
          <Typography variant="body2">{error}</Typography>
          <Button
            size="small"
            onClick={validateImport}
            sx={{ mt: 1 }}
          >
            Retry Validation
          </Button>
        </Alert>
      )}

      {/* Validation Results */}
      {validationSummary && !validating && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Validation Results
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Valid Records */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'success.light',
                    border: '1px solid',
                    borderColor: 'success.main',
                  }}
                >
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <CheckCircleIcon color="success" />
                    <Typography variant="h4" color="success.dark">
                      {validationSummary.summary.valid}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Valid Records
                  </Typography>
                </Paper>
              </Grid>

              {/* Warnings */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'warning.light',
                    border: '1px solid',
                    borderColor: 'warning.main',
                  }}
                >
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <WarningIcon color="warning" />
                    <Typography variant="h4" color="warning.dark">
                      {validationSummary.summary.warnings}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Warnings
                  </Typography>
                </Paper>
              </Grid>

              {/* Errors */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'error.light',
                    border: '1px solid',
                    borderColor: 'error.main',
                  }}
                >
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <ErrorIcon color="error" />
                    <Typography variant="h4" color="error.dark">
                      {validationSummary.summary.errors}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Errors
                  </Typography>
                </Paper>
              </Grid>

              {/* Duplicates */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'info.light',
                    border: '1px solid',
                    borderColor: 'info.main',
                  }}
                >
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <DuplicateIcon color="info" />
                    <Typography variant="h4" color="info.dark">
                      {validationSummary.summary.duplicates}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Duplicates
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Summary Stats */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Records: {validationSummary.summary.total}
              </Typography>
              <Typography variant="body2" color="success.main" gutterBottom>
                Ready to Import: {validationSummary.summary.valid + validationSummary.summary.warnings}
              </Typography>
              {validationSummary.summary.errors > 0 && (
                <Typography variant="body2" color="error.main" gutterBottom>
                  Will be skipped: {validationSummary.summary.errors}
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Warnings and Errors Info */}
          {(validationSummary.summary.warnings > 0 || validationSummary.summary.errors > 0 || validationSummary.summary.duplicates > 0) && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Next Steps:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationSummary.summary.errors > 0 && (
                  <li>Records with errors will be skipped during import</li>
                )}
                {validationSummary.summary.warnings > 0 && (
                  <li>Records with warnings can still be imported</li>
                )}
                {validationSummary.summary.duplicates > 0 && (
                  <li>You'll be able to choose how to handle duplicates in the next step</li>
                )}
              </ul>
            </Alert>
          )}

          {!canProceed && (
            <Alert severity="error" sx={{ mb: 3 }}>
              No valid records found. Please check your data and try again.
            </Alert>
          )}
        </>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onBack} variant="outlined" disabled={validating}>
          Back
        </Button>
        <Button
          onClick={handleContinue}
          variant="contained"
          disabled={!canProceed || validating}
          sx={{
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            }
          }}
        >
          Continue to Review
        </Button>
      </Box>
    </Box>
  );
};